/**
 * The Pro demand view: where people look, per listing on offer.
 *
 * Data: GET /api/demand/map | /place/{kind}/{slug} | /top (backend
 * routes/demand.py, aggregates from services/demand). People counts
 * arrive as ranges ({lo, hi}), never exact; places with fewer than 10
 * visitors never arrive at all. Non-Pro callers get the same shape with
 * values null and `locked: true`.
 *
 * Responses are cached per session in memory, so switching map layers
 * out and back in, or reopening a drawer, does not refetch.
 */
import { API_BASE } from "@/lib/api";
import type { Locale } from "@/lib/i18n-config";

export type DemandKind = "rayon" | "metro" | "qesebe" | "residence";
export type DemandSegment = "all" | "sale_apartment" | "rent_apartment" | "sale_house" | "rent_house";
export type DemandWindow = "1w" | "4w";

export interface Range { lo: number; hi: number | null }

export interface DemandPlace {
  kind: DemandKind;
  slug: string;
  name: string;
  lat: number;
  lng: number;
  index: number | null;
  trend: number | null;
  sessions: Range | null;
  active: number | null;
}

export interface DemandMap {
  locked: boolean;
  week: string | null;
  weeks: string[];
  segment: DemandSegment;
  window: DemandWindow;
  residence_window: DemandWindow;
  k_min: number;
  places: Record<DemandKind, DemandPlace[]>;
}

export interface DemandPoint {
  week: string;
  thin: boolean;
  index: number | null;
  trend: number | null;
  sessions: Range | null;
  searches: Range | null;
  active: number | null;
  new: number | null;
  median_ppm: number | null;
}

export interface DemandPlaceDetail {
  locked: boolean;
  kind: DemandKind;
  slug: string;
  name: string;
  segment: DemandSegment;
  window: DemandWindow;
  week: string;
  k_min: number;
  thin: boolean;
  latest: {
    index: number | null; trend: number | null; sessions: Range | null; opens: Range | null;
    searches: Range | null; active: number; new: number; median_ppm: number | null;
  } | null;
  interest: {
    window: DemandWindow; index: number | null; trend: number | null;
    sessions: Range | null; saves: Range | null; follows: Range | null; alerts: Range | null;
  } | null;
  series: DemandPoint[];
  segments: { segment: DemandSegment; thin: boolean; index: number | null; sessions: Range | null; active: number | null }[];
  searches_vs_supply: { rooms: string; searches: Range | null; active: number }[] | null;
}

const cache = new Map<string, Promise<unknown>>();

function getJSON<T>(path: string): Promise<T> {
  const hit = cache.get(path);
  if (hit) return hit as Promise<T>;
  const p = fetch(`${API_BASE}${path}`, { credentials: "include" }).then((r) => {
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`demand ${r.status}`);
    return r.json();
  });
  cache.set(path, p);
  // A failure must not stick: the next call retries.
  p.catch(() => cache.delete(path));
  return p as Promise<T>;
}

/** Signing in or out changes what the API returns: drop the cache. */
export function resetDemandCache() {
  cache.clear();
}

export function fetchDemandMap(segment: DemandSegment, window: DemandWindow, week?: string | null) {
  const q = new URLSearchParams({ segment, window });
  if (week) q.set("week", week);
  return getJSON<DemandMap>(`/demand/map?${q}`);
}

export function fetchDemandPlace(kind: DemandKind | "auto", slug: string, segment: DemandSegment = "all") {
  return getJSON<DemandPlaceDetail | null>(`/demand/place/${kind}/${encodeURIComponent(slug)}?segment=${segment}`);
}

// ── Colours ──────────────────────────────────────────────────────────
// One hue, sky, light to deep (owner, 2026-10-08: the sky-to-violet
// pair was "a bit too much"); emerald / amber / rose stay reserved for
// good / warn / bad prices, indigo for actions. 50 is the average place
// of its kind; the steps are symmetric around it.
export const DEMAND_STEPS: { min: number; color: string }[] = [
  { min: 0, color: "#e0f2fe" },   // well under average
  { min: 38, color: "#bae6fd" },
  { min: 46, color: "#7dd3fc" },  // around average
  { min: 55, color: "#0ea5e9" },
  { min: 67, color: "#0369a1" },  // twice the average and more
];
export const DEMAND_NONE = "#94a3b8";

export function demandColor(index: number | null | undefined): string {
  if (index == null) return DEMAND_NONE;
  let c = DEMAND_STEPS[0].color;
  for (const s of DEMAND_STEPS) if (index >= s.min) c = s.color;
  return c;
}

// ── Formatting ───────────────────────────────────────────────────────

/** "50-99" / "1000+" / "0": a range as text. `tpl` is the locale's
 *  "{lo}-{hi}" pattern. */
export function formatRange(r: Range | null | undefined, tpl: string): string | null {
  if (!r) return null;
  if (r.lo === 0 && r.hi === 0) return "0";
  if (r.hi == null) return `${r.lo}+`;
  return tpl.replace("{lo}", String(r.lo)).replace("{hi}", String(r.hi - 1));
}

/** Monday of a Baku ISO week ("2026-W40"), as a UTC timestamp. */
export function weekMonday(week: string): number {
  const y = Number(week.slice(0, 4));
  const w = Number(week.slice(6));
  const jan4 = Date.UTC(y, 0, 4);
  const dow = (new Date(jan4).getUTCDay() + 6) % 7;
  return jan4 - dow * 86400000 + (w - 1) * 7 * 86400000;
}

const MONTHS_SHORT: Record<Locale, readonly string[]> = {
  az: ["yan", "fev", "mar", "apr", "may", "iyn", "iyl", "avq", "sen", "okt", "noy", "dek"],
  ru: ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

/** "29 sen" for the Monday of the week. */
export function weekLabel(week: string, locale: Locale): string {
  const d = new Date(weekMonday(week));
  return `${d.getUTCDate()} ${MONTHS_SHORT[locale][d.getUTCMonth()]}`;
}

/** Where a place's own page lives. */
export function placeHref(kind: DemandKind, slug: string): string {
  return kind === "residence" ? `/residence/${slug}` : `/trends/${slug}`;
}
